/**
 * SharePanel — email invite creation + invite management.
 *
 * BOOK-02: Email invite sharing with no reader account required.
 *
 * POST /invites — creates invite, sends SES email (wired in 03-05 plan)
 * GET /invites — lists active invites
 * DELETE /invites/{inviteId} — revokes invite
 */
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../lib/api';

interface Invite {
  id: string;
  recipientEmail: string;
  status: 'active' | 'expired';
  expiresAt: string;
  createdAt: string;
}

export function SharePanel() {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showInvites, setShowInvites] = useState(false);

  const REST_URL = import.meta.env.VITE_REST_API_URL;

  const loadInvites = async () => {
    try {
      const res = await fetchWithAuth(`${REST_URL}invites`);
      const data = await res.json() as { invites: Invite[] };
      setInvites(data.invites ?? []);
    } catch {
      // Non-fatal — invites list will be empty
    }
  };

  useEffect(() => {
    loadInvites();
  }, []);

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const res = await fetchWithAuth(`${REST_URL}invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!res.ok) throw new Error('Failed to send invite');

      setSuccessMessage(`Invite sent to ${email.trim()}`);
      setEmail('');
      await loadInvites();
    } catch {
      setError('Could not send invite. Check your email and try again.');
    } finally {
      setSending(false);
    }
  };

  const revokeInvite = async (inviteId: string, recipientEmail: string) => {
    if (!window.confirm(`Revoke invite: Remove ${recipientEmail}'s access immediately?`)) return;
    setRevoking(inviteId);

    try {
      await fetchWithAuth(`${REST_URL}invites/${inviteId}`, { method: 'DELETE' });
      setInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch {
      setError('Failed to revoke invite. Try again.');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 space-y-4">
      <h2 className="text-lg font-semibold text-stone-900">Share your memoir</h2>

      {error && (
        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">{error}</div>
      )}
      {successMessage && (
        <div className="text-green-700 bg-green-50 border border-green-200 rounded p-3 text-sm">{successMessage}</div>
      )}

      <form onSubmit={sendInvite} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="friend@example.com"
          required
          className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
        />
        <button
          type="submit"
          disabled={sending || !email.trim()}
          className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 font-semibold text-sm hover:bg-stone-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? 'Sending...' : 'Send Invite'}
        </button>
      </form>

      {/* Manage invites — collapsible */}
      {invites.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowInvites(!showInvites)}
            className="text-stone-500 text-sm hover:text-stone-700"
          >
            Manage invites ({invites.length})
          </button>

          {showInvites && (
            <ul className="mt-3 space-y-2">
              {invites.map(invite => (
                <li key={invite.id} className="flex items-center justify-between text-sm py-2 border-b border-stone-100 last:border-0">
                  <div>
                    <span className="text-stone-900">{invite.recipientEmail}</span>
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                      invite.status === 'active'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-stone-100 text-stone-500'
                    }`}>
                      {invite.status === 'active' ? 'Active' : 'Expired'}
                    </span>
                  </div>
                  {invite.status === 'active' && (
                    <button
                      type="button"
                      onClick={() => revokeInvite(invite.id, invite.recipientEmail)}
                      disabled={revoking === invite.id}
                      className="text-red-600 hover:text-red-700 text-sm underline disabled:opacity-50"
                    >
                      {revoking === invite.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
